import { useEffect, useState } from "react";

/**
 * Minimalist B&W animated weather glyph.
 *
 * One pure-SVG component with CSS keyframes for every supported
 * `condition_key`. Rendered next to the colored category dot in card
 * group headers. Honours `prefers-reduced-motion: reduce` by rendering
 * the static SVG with no animation.
 *
 * Spec lifted from the design brief: pure #0A0A0A, transparent
 * background, 1.5px stroke, transform/opacity-only keyframes, no flair.
 */
const STROKE = "#0A0A0A";
const SW = 1.5;

export default function WeatherGlyph({ condition_key, size = 36, title }) {
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReducedMotion(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);

    const k = (condition_key || "cloudy").toLowerCase();
    const cls = reducedMotion ? "wglyph wglyph--static" : "wglyph";

    return (
        <span
            className={cls}
            style={{ width: size, height: size, display: "inline-block" }}
            data-testid={`weather-glyph-${k}`}
            data-condition={k}
            aria-label={title || k.replace(/_/g, " ")}
            title={title || k.replace(/_/g, " ")}
        >
            <Glyph k={k} size={size} />
            <style>{glyphCss}</style>
        </span>
    );
}

function Glyph({ k, size }) {
    const v = "0 0 36 36";
    const common = { width: size, height: size, viewBox: v, fill: "none", stroke: STROKE, strokeWidth: SW, strokeLinecap: "round", strokeLinejoin: "round" };
    if (k === "sunny") return <SunnyGlyph {...common} />;
    if (k === "clear_night") return <NightGlyph {...common} />;
    if (k === "partly_cloudy") return <PartlyCloudyGlyph {...common} />;
    if (k === "overcast") return <OvercastGlyph {...common} />;
    if (k === "rainy") return <RainyGlyph {...common} />;
    if (k === "stormy") return <StormyGlyph {...common} />;
    if (k === "snowy") return <SnowyGlyph {...common} />;
    if (k === "foggy") return <FoggyGlyph {...common} />;
    return <CloudyGlyph {...common} />;
}

// ---- Reusable shapes ----
function Cloud({ cx = 18, cy = 22, scale = 1, className }) {
    // Three-bump cloud, 18 wide × 8 tall, top of cloud at cy-4.
    const t = `translate(${cx} ${cy}) scale(${scale})`;
    return (
        <g transform={t} className={className}>
            <path d="M -8 0 q -2 -7 5 -7 q 2 -5 7 -3 q 4 -3 7 2 q 5 0 4 6 q -2 3 -5 2 z" />
        </g>
    );
}

// ---- Glyph components ----
function SunnyGlyph(p) {
    return (
        <svg {...p}>
            <g className="wg-sun">
                <circle cx="18" cy="18" r="6" />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                    <line
                        key={deg}
                        x1="18"
                        y1="6"
                        x2="18"
                        y2="9.5"
                        transform={`rotate(${deg} 18 18)`}
                    />
                ))}
            </g>
        </svg>
    );
}

function NightGlyph(p) {
    return (
        <svg {...p}>
            <path d="M 22 9 a 9 9 0 1 0 6 13 a 7 7 0 0 1 -6 -13 z" />
            <circle cx="11" cy="12" r="0.8" fill={STROKE} className="wg-star wg-star--a" />
            <circle cx="9" cy="20" r="0.8" fill={STROKE} className="wg-star wg-star--b" />
        </svg>
    );
}

function PartlyCloudyGlyph(p) {
    return (
        <svg {...p}>
            <g className="wg-sun-static">
                <circle cx="13" cy="13" r="4" />
                {[0, 90, 180, 270].map((deg) => (
                    <line
                        key={deg}
                        x1="13"
                        y1="6"
                        x2="13"
                        y2="8"
                        transform={`rotate(${deg} 13 13)`}
                    />
                ))}
            </g>
            <g className="wg-cloud-drift">
                <Cloud cx="22" cy="24" scale={0.85} />
            </g>
        </svg>
    );
}

function CloudyGlyph(p) {
    return (
        <svg {...p}>
            <g className="wg-cloud-drift">
                <Cloud cx="18" cy="20" scale={1.1} />
            </g>
        </svg>
    );
}

function OvercastGlyph(p) {
    return (
        <svg {...p}>
            <g className="wg-cloud-drift wg-cloud-drift--slow">
                <Cloud cx="14" cy="16" scale={0.9} />
            </g>
            <g className="wg-cloud-drift">
                <Cloud cx="22" cy="22" scale={1.05} />
            </g>
        </svg>
    );
}

function RainyGlyph(p) {
    return (
        <svg {...p}>
            <Cloud cx="18" cy="14" scale={1.05} />
            {[
                { x: 12, d: 0 },
                { x: 16, d: 0.35 },
                { x: 20, d: 0.7 },
                { x: 24, d: 1.05 },
            ].map((r) => (
                <line
                    key={r.x}
                    x1={r.x}
                    y1="20"
                    x2={r.x}
                    y2="24"
                    className="wg-rain-drop"
                    style={{ animationDelay: `${r.d}s` }}
                />
            ))}
        </svg>
    );
}

function StormyGlyph(p) {
    return (
        <svg {...p}>
            <Cloud cx="18" cy="14" scale={1.05} />
            <path
                d="M 18 19 l -3 5 l 3 0 l -2 5 l 5 -6 l -3 0 l 2 -4 z"
                fill={STROKE}
                stroke="none"
                className="wg-bolt"
            />
        </svg>
    );
}

function SnowyGlyph(p) {
    return (
        <svg {...p}>
            <Cloud cx="18" cy="14" scale={1.05} />
            {[
                { x: 12, d: 0 },
                { x: 16, d: 0.6 },
                { x: 20, d: 1.2 },
                { x: 24, d: 1.8 },
            ].map((r) => (
                <circle
                    key={r.x}
                    cx={r.x}
                    cy="22"
                    r="0.9"
                    fill={STROKE}
                    stroke="none"
                    className="wg-snow-flake"
                    style={{ animationDelay: `${r.d}s` }}
                />
            ))}
        </svg>
    );
}

function FoggyGlyph(p) {
    return (
        <svg {...p}>
            <line x1="6" y1="13" x2="30" y2="13" className="wg-fog wg-fog--a" />
            <line x1="6" y1="19" x2="30" y2="19" className="wg-fog wg-fog--b" />
            <line x1="6" y1="25" x2="30" y2="25" className="wg-fog wg-fog--c" />
        </svg>
    );
}

// ---- CSS keyframes (transform + opacity only) ----
const glyphCss = `
@keyframes wg-sun-pulse { 0%,100% { transform: scale(0.92); } 50% { transform: scale(1.08); } }
.wglyph .wg-sun { transform-origin: 18px 18px; animation: wg-sun-pulse 4s ease-in-out infinite; }

@keyframes wg-star-fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
.wglyph .wg-star { animation: wg-star-fade 3s ease-in-out infinite; }
.wglyph .wg-star--b { animation-delay: 1.5s; }

@keyframes wg-cloud-drift { 0%,100% { transform: translateX(-2px); } 50% { transform: translateX(2px); } }
.wglyph .wg-cloud-drift { animation: wg-cloud-drift 6s ease-in-out infinite; }
.wglyph .wg-cloud-drift--slow { animation-duration: 8s; }

@keyframes wg-rain { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(8px); opacity: 0; } }
.wglyph .wg-rain-drop { animation: wg-rain 1.4s linear infinite; transform-origin: center; }

@keyframes wg-bolt-flash { 0%, 92%, 100% { opacity: 0; } 94%, 96% { opacity: 1; } }
.wglyph .wg-bolt { animation: wg-bolt-flash 3s linear infinite; }

@keyframes wg-snow { 0% { transform: translate(0, 0); opacity: 1; } 100% { transform: translate(0, 8px); opacity: 0; } }
.wglyph .wg-snow-flake { animation: wg-snow 3s linear infinite; transform-origin: center; }

@keyframes wg-fog { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
.wglyph .wg-fog { animation: wg-fog 3s ease-in-out infinite; }
.wglyph .wg-fog--b { animation-delay: 0.5s; }
.wglyph .wg-fog--c { animation-delay: 1s; }

.wglyph--static * { animation: none !important; }

@media (prefers-reduced-motion: reduce) {
    .wglyph * { animation: none !important; }
}
`;
